import { useEffect } from 'react'

/** Keep inline editors reachable when a keyboard covers the layout viewport. */
export function useMobileFormViewport() {
  useEffect(() => {
    const viewport = window.visualViewport, root = document.documentElement
    let frame = 0
    let observed: Element | null = null
    const observer = new ResizeObserver(() => schedule())
    const mutations = new MutationObserver(() => schedule())
    const update = () => {
      frame = 0
      const field = document.activeElement
      const editing = field instanceof HTMLElement && field.matches('input:not([type="checkbox"]):not([type="radio"]),textarea,[contenteditable="true"]')
      const currentViewport = window.visualViewport
      const height = currentViewport?.height || innerHeight, offset = currentViewport?.offsetTop || 0
      // Safari can shrink only visualViewport. Extra document space lets the
      // last inline field scroll above that occlusion, including in a PWA.
      const inset = editing && !field.closest('[role="dialog"]') ? Math.max(0, innerHeight - height - offset) : 0
      root.style.setProperty('--app-keyboard-inset', `${inset}px`)
      const content = editing && !field.closest('[role="dialog"]') ? field.closest('main') : null
      if (content !== observed) {
        observer.disconnect(); mutations.disconnect(); observed = content
        if (content) {
          observer.observe(content)
          // Search results and validation can move a field without changing
          // the page's overall height. Recheck after those DOM updates too.
          mutations.observe(content, { childList: true, subtree: true, characterData: true })
        }
      }
      if (!editing || field.closest('[role="dialog"]')) return
      const shell = document.querySelector('.app-shell')
      const safeTop = shell ? parseFloat(getComputedStyle(shell).paddingTop) || 0 : 0
      const nav = document.querySelector('nav[aria-label="Основные разделы"]')?.getBoundingClientRect()
      const top = offset + safeTop + 12
      const bottom = Math.min(offset + height, nav && nav.top > top && nav.top < offset + height ? nav.top : Infinity) - 12
      const rect = field.getBoundingClientRect()
      const visibleHeight = Math.min(rect.height, Math.max(44, bottom - top))
      const delta = rect.top < top ? rect.top - top : rect.top + visibleHeight > bottom ? rect.top + visibleHeight - bottom : 0
      // Scroll document content explicitly. On mobile, window.scrollBy may
      // pan the visual viewport first instead of moving the focused field.
      if (Math.abs(delta) > 1 && document.scrollingElement) document.scrollingElement.scrollTop += delta
    }
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update) }
    document.addEventListener('focusin', schedule, true)
    document.addEventListener('focusout', schedule)
    window.addEventListener('resize', schedule)
    viewport?.addEventListener('resize', schedule)
    viewport?.addEventListener('scroll', schedule)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      mutations.disconnect()
      document.removeEventListener('focusin', schedule, true)
      document.removeEventListener('focusout', schedule)
      window.removeEventListener('resize', schedule)
      viewport?.removeEventListener('resize', schedule)
      viewport?.removeEventListener('scroll', schedule)
      root.style.removeProperty('--app-keyboard-inset')
    }
  }, [])
}
